import { Observable, defer, from, fromEvent, map, of, shareReplay } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthProvider } from '@core/interfaces/auth-provider';
import type { PlatformAPI } from '@ol/kit';

function platform(): PlatformAPI {
  const api = (window as unknown as { __platform__?: PlatformAPI })['__platform__'];
  if (!api) throw new Error('[designer-embed] window.__platform__ is not installed');
  return api;
}

// Translates Designer's classic AuthProvider surface onto Hub's PlatformAPI.
// Used inside the embedded AppEmbedModule so that no part of Designer reaches
// for @auth0/auth0-angular directly when running under the platform shell.
export class PlatformAuthProvider implements AuthProvider {
  private readonly user$ = defer(() => of(platform().auth.user())).pipe(shareReplay(1));

  getToken(): Observable<string> {
    return defer(() => from(platform().auth.getValidAccessToken())).pipe(
      filter((t): t is string => t !== null),
    );
  }

  getAccessTokenSilently(): Observable<string> {
    return this.getToken();
  }

  getUser$(): Observable<unknown> {
    return this.user$;
  }

  isLoggedIn(): Observable<boolean> {
    return defer(() => of(platform().auth.isAuthenticated()));
  }

  login(redirectPath: string): void {
    void platform().auth.login(redirectPath);
  }

  logout(): void {
    void platform().auth.logout();
  }

  getError$(): Observable<unknown> {
    // Auth errors are handled by Hub; surface a never-emitting stream to keep
    // existing subscribers wired without firing in embed mode.
    return fromEvent(window, '__designer_embed_never__').pipe(map(() => undefined as unknown));
  }
}
